import type { AuthResponse } from "@/lib/api/auth";
import { apiRequest } from "@/lib/http";

export type MfaFactorMethod = "TOTP" | "RECOVERY_CODE";

/** Login methods incl. passkeys; UI state only. */
export type MfaLoginMethod = MfaFactorMethod | "PASSKEY";

export type MfaChallenge = {
  token: string;
  expiresAt: string;
};

export type MfaPasskeySummary = {
  id: string;
  displayName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  deviceType: "SINGLE_DEVICE" | "MULTI_DEVICE";
  backedUp: boolean;
};

export type MfaFactors = {
  totp: { active: boolean; confirmedAt: string | null };
  passkeys: MfaPasskeySummary[];
};

export type MfaStatus = {
  status: "MFA_STATUS";
  enabled: boolean;
  recoveryCodesRemaining: number;
  enforceAt: string | null;
  gracePeriodActive: boolean;
  setupRequired: boolean;
  factors?: MfaFactors;
};

export type MfaSetup = {
  otpauthUri: string;
  manualSecret: string;
  expiresAt: string;
};

export type MfaEnrollmentStarted = MfaSetup & {
  status: "MFA_ENROLLMENT_STARTED";
  expiresInSeconds: number;
};

export type MfaRecoveryCodes = {
  status:
    | "MFA_RECOVERY_CODES_REQUIRED"
    | "MFA_DEVICE_CHANGE_RECOVERY_CODES_REQUIRED";
  recoveryCodes: string[];
};

export type MfaCompletion = {
  status: "MFA_ENROLLMENT_COMPLETED" | "MFA_DEVICE_CHANGE_COMPLETED";
  loginRequired: true;
  revokedSessionCount: number;
};

export type MfaDeviceChangeStarted = MfaSetup & {
  status: "MFA_DEVICE_CHANGE_STARTED";
  challengeToken: string;
};

export function verifyMfaLogin(input: {
  challengeToken: string;
  method: MfaFactorMethod;
  code: string;
}) {
  return apiRequest<AuthResponse>("/auth/mfa/login/verify", {
    method: "POST",
    body: JSON.stringify(input),
    skipAuth: true,
  });
}

export function getMfaStatus() {
  return apiRequest<MfaStatus>("/auth/mfa/status");
}

export function issueMfaEnrollmentChallenge() {
  return apiRequest<MfaChallenge>("/auth/mfa/enrollment/challenge", {
    method: "POST",
  });
}

export function startMfaEnrollment(challengeToken: string) {
  return apiRequest<MfaEnrollmentStarted>("/auth/mfa/enrollment/start", {
    method: "POST",
    body: JSON.stringify({ challengeToken }),
    skipAuth: true,
  });
}

export function verifyMfaEnrollment(input: {
  challengeToken: string;
  code: string;
}) {
  return apiRequest<MfaRecoveryCodes>("/auth/mfa/enrollment/verify", {
    method: "POST",
    body: JSON.stringify(input),
    skipAuth: true,
  });
}

export function completeMfaEnrollment(challengeToken: string) {
  return apiRequest<MfaCompletion>("/auth/mfa/enrollment/complete", {
    method: "POST",
    body: JSON.stringify({ challengeToken, recoveryCodesStored: true }),
    skipAuth: true,
  });
}

export function startMfaDeviceChange(input: {
  password: string;
  method: MfaFactorMethod;
  code: string;
}) {
  return apiRequest<MfaDeviceChangeStarted>("/auth/mfa/device-change/start", {
    method: "POST",
    body: JSON.stringify(input),
    skipAuthRefresh: true,
  });
}

export function verifyMfaDeviceChange(input: {
  challengeToken: string;
  code: string;
}) {
  return apiRequest<MfaRecoveryCodes>("/auth/mfa/device-change/verify", {
    method: "POST",
    body: JSON.stringify(input),
    skipAuthRefresh: true,
  });
}

export function completeMfaDeviceChange(challengeToken: string) {
  return apiRequest<MfaCompletion>("/auth/mfa/device-change/complete", {
    method: "POST",
    body: JSON.stringify({ challengeToken, recoveryCodesStored: true }),
    skipAuthRefresh: true,
  });
}

// --------------------------------------------------------------------------
// Passkeys release: new endpoints only -- the pinned bodies above stay
// byte-identical.
// --------------------------------------------------------------------------

export type MfaWebauthnCeremony = {
  status: string;
  options: Record<string, unknown>;
  expiresAt: string;
};

export type MfaStepUpProof = {
  password: string;
  method: MfaLoginMethod;
  code?: string;
  stepUpToken?: string;
  credential?: Record<string, unknown>;
};

export type MfaStepUpCeremony = {
  status: "MFA_STEP_UP_OPTIONS";
  stepUpToken: string;
  options: Record<string, unknown>;
  expiresAt: string;
};

export type MfaPasskeyRegistrationStarted = {
  status: "WEBAUTHN_REGISTRATION_STARTED";
  challengeToken: string;
  expiresAt: string;
};

export type MfaPasskeyRegistrationVerified =
  | { status: "MFA_RECOVERY_CODES_REQUIRED"; recoveryCodes: string[] }
  | {
      status: "WEBAUTHN_CREDENTIAL_ADDED";
      credential: {
        id: string;
        displayName: string | null;
        createdAt: string;
        deviceType: "SINGLE_DEVICE" | "MULTI_DEVICE";
        backedUp: boolean;
      };
    };

export type MfaPasskeyEnrollmentCompleted = {
  status: "WEBAUTHN_ENROLLMENT_COMPLETED";
  loginRequired: true;
  revokedSessionCount: number;
};

export function getMfaPasskeyLoginOptions(challengeToken: string) {
  return apiRequest<MfaWebauthnCeremony>("/auth/mfa/webauthn/login/options", {
    method: "POST",
    body: JSON.stringify({ challengeToken }),
    skipAuth: true,
  });
}

export function verifyMfaPasskeyLogin(input: {
  challengeToken: string;
  credential: Record<string, unknown>;
}) {
  return apiRequest<AuthResponse>("/auth/mfa/webauthn/login/verify", {
    method: "POST",
    body: JSON.stringify(input),
    skipAuth: true,
  });
}

export function getMfaPasskeyRegistrationOptions(challengeToken: string) {
  return apiRequest<MfaWebauthnCeremony>(
    "/auth/mfa/webauthn/register/options",
    {
      method: "POST",
      body: JSON.stringify({ challengeToken }),
      skipAuth: true,
    },
  );
}

export function verifyMfaPasskeyRegistration(input: {
  challengeToken: string;
  displayName?: string;
  credential: Record<string, unknown>;
}) {
  return apiRequest<MfaPasskeyRegistrationVerified>(
    "/auth/mfa/webauthn/register/verify",
    {
      method: "POST",
      body: JSON.stringify(input),
      skipAuth: true,
    },
  );
}

export function completeMfaPasskeyEnrollment(challengeToken: string) {
  return apiRequest<MfaPasskeyEnrollmentCompleted>(
    "/auth/mfa/webauthn/register/complete",
    {
      method: "POST",
      body: JSON.stringify({ challengeToken, recoveryCodesStored: true }),
      skipAuth: true,
    },
  );
}

export function startMfaPasskeyRegistration(proof: MfaStepUpProof) {
  return apiRequest<MfaPasskeyRegistrationStarted>(
    "/auth/mfa/webauthn/register/start",
    {
      method: "POST",
      body: JSON.stringify(proof),
      skipAuthRefresh: true,
    },
  );
}

export function getMfaStepUpOptions() {
  return apiRequest<MfaStepUpCeremony>("/auth/mfa/stepup/options", {
    method: "POST",
    body: JSON.stringify({}),
    skipAuthRefresh: true,
  });
}

export function renameMfaPasskey(id: string, displayName: string) {
  return apiRequest<{
    status: "WEBAUTHN_CREDENTIAL_RENAMED";
    credential: { id: string; displayName: string };
  }>(`/auth/mfa/webauthn/credentials/${id}/rename`, {
    method: "POST",
    body: JSON.stringify({ displayName }),
    skipAuthRefresh: true,
  });
}

export function deleteMfaPasskey(id: string, proof: MfaStepUpProof) {
  return apiRequest<{ status: "WEBAUTHN_CREDENTIAL_REMOVED" }>(
    `/auth/mfa/webauthn/credentials/${id}/delete`,
    {
      method: "POST",
      body: JSON.stringify(proof),
      skipAuthRefresh: true,
    },
  );
}

export function deleteMfaTotp(proof: MfaStepUpProof) {
  return apiRequest<{ status: "MFA_TOTP_REMOVED" }>("/auth/mfa/totp/delete", {
    method: "POST",
    body: JSON.stringify(proof),
    skipAuthRefresh: true,
  });
}
